import { Alert, Snackbar } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EnhancedTable from "../../../Components/EnhancedTable";
import IncomeHeader from "../../../Components/IncomeHeader";
import RecievableSummary from "../../../Components/RecievableSummary";
import useAuthStore from "../../../store/useAuthStore";

const headCells = [
  { id: "header", numeric: true, disablePadding: true, label: "Income Header" },
  { id: "subheader", numeric: true, disablePadding: true, label: "Income Subheader" },
  { id: "amount", numeric: true, disablePadding: false, label: "Amount (Nu)" },
  { id: "description", numeric: false, disablePadding: false, label: "Description" },
  { id: "bankName", numeric: false, disablePadding: false, label: "Bank" },
  { id: "paymentMethod", numeric: false, disablePadding: false, label: "Payment Method" },
  { id: "date", numeric: true, disablePadding: false, label: "Date" },
];

const Income = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateRange, setDateRange] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const hasLoadedOnce = useRef(false);

  const { token } = useAuthStore();

  const fetchIncomes = React.useCallback(async () => {
    setLoading(true);
    try {
      const formatDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      };

      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        startDate: startDate ? formatDate(startDate) : "",
        endDate: endDate ? formatDate(endDate) : "",
      });

      const response = await fetch(`/api/getallIncomes?${queryParams}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch Incomes");

      const data = await response.json();
      setRows(
        data.incomes.map((income) => ({
          id: income._id,
          header: income.header,
          subheader: income.taxRate ? `${income.subheader} (${income.taxRate}%)` : income.subheader,
          amount: income.amount,
          description: income.description,
          bankName: income.bankName || "-",
          paymentMethod: income.paymentMethod || "-",
          date: income.date,
        }))
      );
      setTotalCount(data.totalCount || 0);
      hasLoadedOnce.current = true;
    } catch (error) {
      console.error("Error fetching incomes: ", error);
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    fetchIncomes();
  }, [fetchIncomes]);

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleEdit = (row) => navigate("/editIncome", { state: { row } });
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event, newRowsPerPage) => { setRowsPerPage(newRowsPerPage); setPage(0); };

  const handleDeleteIncomes = async (selectedIds) => {
    try {
      const response = await fetch("/api/deleteIncome", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Failed to delete Incomes");
      fetchIncomes();
      setSnackbar({ open: true, message: "Incomes deleted successfully!", severity: "success" });
    } catch (error) {
      console.error("Error deleting Incomes:", error);
      setSnackbar({ open: true, message: error.message || "Failed to delete Incomes", severity: "error" });
    }
  };

  return (
    <div>
      <IncomeHeader className="mb-4" />

      <RecievableSummary
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        dateRange={dateRange}
        setDateRange={setDateRange}
        visibleRows={rows}
        headCells={headCells}
      />

      {/* Table area — skeleton on first load, overlay spinner on refetch */}
      {!hasLoadedOnce.current && loading ? (
        <div className="animate-pulse space-y-3 mt-2">
          <div className="bg-gray-200 h-10 rounded-lg w-full" />
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-gray-100 h-8 rounded w-full" />
          ))}
        </div>
      ) : (
        <div className="relative">
          {loading && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-lg">
              <div className="flex items-center gap-2 text-blue-600">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <span className="text-sm font-medium">Loading...</span>
              </div>
            </div>
          )}
          <EnhancedTable
            rows={rows}
            headCells={headCells}
            onDelete={handleDeleteIncomes}
            onEdit={handleEdit}
            serverSide={true}
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            color: "white",
            backgroundColor: snackbar.severity === "success" ? "#22c55e" : "#ef4444",
            "& .MuiAlert-icon": { color: "white" },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Income;
